import { FocusEventHandler, useMemo } from "react";

import { FieldValues, Path, UseFormReturn, useController } from "react-hook-form";

import { getChoiceDisabled } from "../../../../components/CustomAnswer/choices.helper";
import { FormCheckbox } from "../../../../components/forms-design-system/FormCheckbox";
import { FormCheckboxGroup } from "../../../../components/forms-design-system/FormCheckboxGroup";
import { isRequired } from "../../../../helpers/validators";
import { useTranslate } from "../../../../hooks/useTranslate";

import { QuestionFieldChoice } from "./QuestionFieldChoice";
import { useAnswersOfParticipantsSelectedChoicesCount } from "./useAnswersOfParticipantsSelectedChoicesCount";

import { QuestionOutput } from "@/types/trpc";

export interface Props<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  question: QuestionOutput;
  label: string;
  answersOfParticipantsSelectedChoicesCount?: Record<string, number>;
}

export function QuestionFieldMultipleCheckboxes<T extends FieldValues>({ question, form, name, label }: Props<T>) {
  const { t } = useTranslate();

  const sortedQuestionChoices = useMemo(
    () => question.choices?.slice().sort((choiceA, choiceB) => choiceA.sortIndex - choiceB.sortIndex) || [],
    [question.choices]
  );

  const answersOfParticipantsSelectedChoicesCount = useAnswersOfParticipantsSelectedChoicesCount(form);

  const {
    field: { value = [] as string[], onChange, onBlur, ref },
    fieldState: { error },
  } = useController({
    name,
    control: form.control,
    rules: isRequired(question.required, t("common", "answerRequired")),
  });

  const choicesErrorMessage = error?.message;

  return (
    <FormCheckboxGroup
      data-test="question-field-multiple-checkboxes"
      label={label}
      value={value || []}
      onChange={onChange}
      error={Boolean(choicesErrorMessage)}
      helperText={choicesErrorMessage}
      ref={ref}
      required={question.required}
      onBlur={onBlur as FocusEventHandler}
    >
      {sortedQuestionChoices.map((choice, index) => (
        <FormCheckbox
          key={choice.id}
          id={choice.id}
          name={`${name}.answers.${index}`}
          value={choice.id}
          disabled={!value?.includes(choice.id) && getChoiceDisabled(choice, answersOfParticipantsSelectedChoicesCount)}
          label={
            <QuestionFieldChoice
              choice={choice}
              answersOfParticipantsSelectedChoicesCount={answersOfParticipantsSelectedChoicesCount}
            />
          }
        />
      ))}
    </FormCheckboxGroup>
  );
}
